import React, { useEffect, useState } from 'react';
import { Form, Input, Select, DatePicker, Row, Col } from 'antd';
import dayjs from 'dayjs';
import { BasicInfoData } from './index';
import { getStaff } from '../../../../services/user';
import { formRules } from '../../../../utils/validation';
import { getCurrentUser } from '../../../../services/auth';
import { UserRole } from '../../../../constants/user';
import WechatGroupSelect from '../../../../components/WechatGroupSelect';
import LeadSelect from '../../../../components/LeadSelect';

const { Option } = Select;

interface BasicInfoStepProps {
  data: Partial<BasicInfoData>;
  onChange: (data: Partial<BasicInfoData>) => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ data, onChange }) => {
  const [form] = Form.useForm();
  const [lawyers, setLawyers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    loadCurrentUser();
    loadLawyers();
  }, []);

  useEffect(() => {
    // Initialize form with data
    const formValues: any = {
      case_name: data.case_name,
      wechat_group_id: data.wechat_group_id,
      responsible_lawyer_id: data.responsible_lawyer_id || currentUser?.id, // 默认为当前用户
      assistant_lawyer_id: data.assistant_lawyer_id,
      lead_id: data.lead_id,
      case_status: (data as any).case_status || 'active',
    };
    
    // Only set accept_date if it exists
    if (data.accept_date) {
      formValues.accept_date = dayjs(data.accept_date);
    }
    
    form.setFieldsValue(formValues);
  }, [data, form, currentUser]);

  const loadCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      setIsAdmin(user.role === UserRole.ADMIN);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  const loadLawyers = async () => {
    setLoading(true);
    try {
      const users = await getStaff({ limit: 1000 });
      setLawyers(users);
    } catch (error) {
      console.error('Failed to load lawyers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValuesChange = (_: any, allValues: any) => {
    // Convert dayjs objects to strings
    const formattedValues: any = {
      case_name: allValues.case_name,
      wechat_group_id: allValues.wechat_group_id,
      responsible_lawyer_id: allValues.responsible_lawyer_id,
      assistant_lawyer_id: allValues.assistant_lawyer_id,
      lead_id: allValues.lead_id,
      case_status: allValues.case_status || 'active',
    };
    
    // Only include accept_date if it exists
    if (allValues.accept_date) {
      formattedValues.accept_date = allValues.accept_date.format('YYYY-MM-DD');
    }
    
    onChange(formattedValues);
  };



  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="关联企业微信群"
            name="wechat_group_id"
            tooltip="选择与此案件关联的企业微信群聊，案件名称将自动使用群聊名称"
          >
            <WechatGroupSelect
              onChange={(value, groupName) => {
                // 当选择群聊时，自动填充案件名称
                if (value && groupName) {
                  form.setFieldsValue({ case_name: groupName });
                  // 触发 onChange 更新父组件数据
                  const allValues = form.getFieldsValue();
                  onChange({ ...allValues, case_name: groupName });
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="案件名称"
            name="case_name"
            rules={[
              formRules.required('请输入案件名称'),
              { max: 200, message: '案件名称不能超过200个字符' },
              { whitespace: true, message: '案件名称不能为空格' },
            ]}
          >
            <Input placeholder="请输入案件名称或选择群聊自动填充" maxLength={200} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="承接日期"
            name="accept_date"
            rules={[formRules.required('请选择承接日期')]}
          >
            <DatePicker style={{ width: '100%' }} placeholder="请选择承接日期" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="案件状态"
            name="case_status"
            initialValue="active"
          >
            <Select placeholder="请选择案件状态">
              <Option value="active">进行中</Option>
              <Option value="closed">已结案</Option>
              <Option value="suspended">已暂停</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="负责律师"
            name="responsible_lawyer_id"
            rules={[formRules.required('请选择负责律师')]}
            tooltip={!isAdmin ? "普通用户不可修改负责律师" : undefined}
          >
            <Select
              placeholder="请选择负责律师"
              loading={loading}
              disabled={!isAdmin}
              showSearch
              filterOption={(input: string, option: any) => {
                const label = option?.children;
                if (typeof label === 'string') {
                  return label.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
            >
              {lawyers.map((lawyer) => (
                <Option key={lawyer.id} value={lawyer.id}>
                  {lawyer.real_name || lawyer.username}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item label="助理律师" name="assistant_lawyer_id">
            <Select
              placeholder="请选择助理律师（可选）"
              loading={loading}
              allowClear
              showSearch
              filterOption={(input: string, option: any) => {
                const label = option?.children;
                if (typeof label === 'string') {
                  return label.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
            >
              {lawyers.map((lawyer) => (
                <Option key={lawyer.id} value={lawyer.id}>
                  {lawyer.real_name || lawyer.username}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>



      <Row gutter={16}>
        <Col span={24}>
          <Form.Item 
            label="关联线索" 
            name="lead_id" 
            tooltip="如果此案件来自某个线索，可以关联。支持按联系人/手机号搜索"
          >
            <LeadSelect placeholder="请选择关联线索（可选）" />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default BasicInfoStep;
